import { FC } from "react";

import { Box, Tooltip, Typography, alpha, useTheme } from "@mui/material";

const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{6})$/;

const normalizeHexColor = (value: unknown): string => {
	if (typeof value !== "string") return "";
	const trimmed = value.trim();
	return HEX_COLOR_REGEX.test(trimmed) ? trimmed : "";
};

type ShiftMetaBadgeProps = {
	label: string;
	color?: unknown;
	compact?: boolean;
	maxWidth?: number;
};

export const ShiftMetaBadge: FC<ShiftMetaBadgeProps> = ({
	label,
	color,
	compact = false,
	maxWidth,
}) => {
	const theme = useTheme();
	const resolvedColor = normalizeHexColor(color);
	const hasColor = Boolean(resolvedColor);
	const neutralBorder = alpha(
		theme.palette.text.primary,
		theme.palette.mode === "dark" ? 0.34 : 0.22,
	);

	return (
		<Tooltip title={label} arrow>
			<Box
				sx={{
					display: "inline-flex",
					alignItems: "center",
					gap: compact ? 0.5 : 0.75,
					px: compact ? 1 : 1.25,
					py: compact ? 0.25 : 0.5,
					borderRadius: compact ? 1 : 2,
					bgcolor: hasColor ? resolvedColor : "transparent",
					border: "1px solid",
					borderStyle: hasColor ? "solid" : "dashed",
					borderColor: hasColor ? alpha(resolvedColor, 0.6) : neutralBorder,
					maxWidth: maxWidth ? `${maxWidth}px` : "100%",
					minWidth: 0,
					cursor: "default",
					whiteSpace: "nowrap",
					color: "text.primary",
				}}
			>
				<Box
					sx={{
						width: compact ? 6 : 8,
						height: compact ? 6 : 8,
						borderRadius: "50%",
						bgcolor: hasColor
							? alpha(theme.palette.common.black, 0.45)
							: "transparent",
						border: hasColor ? "none" : `1px solid ${neutralBorder}`,
						flexShrink: 0,
					}}
				/>
				<Typography
					variant={compact ? "caption" : "body2"}
					fontWeight={600}
					noWrap
					sx={{
						letterSpacing: compact ? 0.2 : 0.3,
						maxWidth: "100%",
					}}
				>
					{label}
				</Typography>
			</Box>
		</Tooltip>
	);
};
